// Registry of supported source platforms.
import asanaLogo from '@/assets/migration/asana.svg';
import basecampLogo from '@/assets/migration/basecamp.svg';
import clickupLogo from '@/assets/migration/clickup.svg';
import jiraLogo from '@/assets/migration/jira.svg';
import mondayLogo from '@/assets/migration/monday.png';
import msProjectLogo from '@/assets/migration/msproject.png';
import notionLogo from '@/assets/migration/notion.svg';
import smartsheetLogo from '@/assets/migration/smartsheet.png';
import trelloLogo from '@/assets/migration/trello.svg';
import wrikeLogo from '@/assets/migration/wrike.png';
import { parseAsanaCSV } from '@/utils/migration/parsers/asana';
import { parseGenericCSV } from '@/utils/migration/parsers/generic';
import { parseTrelloJSON } from '@/utils/migration/parsers/trello';
import { parseWrikeCSV } from '@/utils/migration/parsers/wrike';
import type { NormalizedImport } from '@/utils/migration/normalizedImport';

export interface MigrationSource {
  id: string;
  name: string;
  emoji: string;
  /** Optional URL to the actual brand logo (preferred over emoji when present). */
  logoUrl?: string;
  supportedFormats: ('csv' | 'json' | 'xlsx')[];
  /** Brief in-app instructions on where to find the export. */
  exportTutorial: string;
  /** Returns a parsed NormalizedImport, or throws. Pass file text. */
  fileParser?: (text: string) => NormalizedImport;
  /** True if iframe-proxy capture mode tends to work for this site. */
  iframeFriendly: boolean;
  loginUrl?: string;
  /** Featured / first-class migration path — surfaced more prominently in the picker. */
  featured?: boolean;
  /** Optional tagline shown under featured sources. */
  tagline?: string;
}

export const MIGRATION_SOURCES: MigrationSource[] = [
  {
    id: 'wrike',
    name: 'Wrike',
    logoUrl: wrikeLogo,
    emoji: '🟩',
    supportedFormats: ['csv', 'xlsx'],
    exportTutorial:
      'In Wrike: open the folder, project or space → ⋯ menu → Export to Excel. ' +
      'Save as .xlsx, then re-save as .csv (UTF-8) and drop it here. ' +
      'We preserve titles, descriptions, assignees, start/finish dates, predecessors, ' +
      'parent tasks, importance flags, custom statuses and folder paths.',
    fileParser: parseWrikeCSV,
    iframeFriendly: false,
    loginUrl: 'https://www.wrike.com/login/',
    featured: true,
    tagline: 'First-class migration — predecessors, importance, custom statuses and folder paths all carry over.',
  },
  {
    id: 'asana',
    name: 'Asana',
    logoUrl: asanaLogo,
    emoji: '🟠',
    supportedFormats: ['csv'],
    exportTutorial: 'Project → ⋯ menu → Export / Print → CSV',
    fileParser: parseAsanaCSV,
    iframeFriendly: false,
    loginUrl: 'https://app.asana.com',
  },
  {
    id: 'trello',
    name: 'Trello',
    logoUrl: trelloLogo,
    emoji: '🟦',
    supportedFormats: ['json'],
    exportTutorial: 'Board → Show menu → More → Print and Export → Export as JSON',
    fileParser: parseTrelloJSON,
    iframeFriendly: true,
    loginUrl: 'https://trello.com',
  },
  {
    id: 'monday',
    name: 'Monday.com',
    logoUrl: mondayLogo,
    emoji: '🟪',
    supportedFormats: ['xlsx', 'csv'],
    exportTutorial: 'Board → ⋯ → More actions → Export to Excel',
    fileParser: parseGenericCSV,
    iframeFriendly: false,
    loginUrl: 'https://monday.com',
  },
  {
    id: 'clickup',
    name: 'ClickUp',
    logoUrl: clickupLogo,
    emoji: '🟡',
    supportedFormats: ['csv'],
    exportTutorial: 'Settings → Import/Export → Export → CSV',
    fileParser: parseGenericCSV,
    iframeFriendly: false,
    loginUrl: 'https://app.clickup.com',
  },
  {
    id: 'jira',
    name: 'Jira',
    logoUrl: jiraLogo,
    emoji: '🔷',
    supportedFormats: ['csv'],
    exportTutorial: 'Filters → All issues → Export → CSV (current fields)',
    fileParser: parseGenericCSV,
    iframeFriendly: false,
    loginUrl: 'https://id.atlassian.com',
  },
  {
    id: 'notion',
    name: 'Notion',
    logoUrl: notionLogo,
    emoji: '⬛',
    supportedFormats: ['csv'],
    exportTutorial: 'Database → ⋯ → Export → Markdown & CSV (we use the CSV)',
    fileParser: parseGenericCSV,
    iframeFriendly: false,
    loginUrl: 'https://notion.so',
  },
  {
    id: 'basecamp',
    name: 'Basecamp',
    logoUrl: basecampLogo,
    emoji: '🟢',
    supportedFormats: ['csv'],
    exportTutorial: 'Account settings → Export your data',
    fileParser: parseGenericCSV,
    iframeFriendly: true,
    loginUrl: 'https://launchpad.37signals.com',
  },
  {
    id: 'msproject',
    name: 'Microsoft Project',
    logoUrl: msProjectLogo,
    emoji: '🟦',
    supportedFormats: ['csv', 'xlsx'],
    exportTutorial: 'File → Save As → CSV (Comma delimited)',
    fileParser: parseGenericCSV,
    iframeFriendly: false,
  },
  {
    id: 'smartsheet',
    name: 'Smartsheet',
    logoUrl: smartsheetLogo,
    emoji: '🟢',
    supportedFormats: ['csv', 'xlsx'],
    exportTutorial: 'File → Export → Export to CSV',
    fileParser: parseGenericCSV,
    iframeFriendly: false,
    loginUrl: 'https://app.smartsheet.com',
  },
  {
    id: 'generic',
    name: 'Generic spreadsheet',
    emoji: '📄',
    supportedFormats: ['csv'],
    exportTutorial: 'Any CSV with a header row. Common columns auto-detected.',
    fileParser: parseGenericCSV,
    iframeFriendly: false,
  },
];

export const getMigrationSource = (id: string) =>
  MIGRATION_SOURCES.find((s) => s.id === id);
